import { IsArray, IsNumber, RandomInteger, Range, Zip } from "@pdfking/tfunc"

import type { RGBColor } from "../types/colors/RGBColor"
import type { Pixel } from "../types/units/Units"

type ImageSize = [Pixel, Pixel] | Pixel
type ColorRange = [RGBColor, RGBColor] | RGBColor

const DEFAULT_SIZE = 150 as Pixel

const BLACK: RGBColor = { r: 0, g: 0, b: 0, a: 1 }
const WHITE: RGBColor = { r: 1, g: 1, b: 1, a: 1 }

const randomChannel = (n: number, min: number, max: number, seed?: number) =>
  RandomInteger(n, Math.round(min * 255), Math.round(max * 255), seed)

const normalizeSize = (size: ImageSize): [Pixel, Pixel] =>
  IsArray(size) ? size : [size, size]

/**
 * Generates a random image with pixel colors in specified RGB range.
 *
 * API inspired by Wolfram Language's RandomImage function.
 *
 * @param color - Color range specification:
 *   - RGBColor: range from black to provided color
 *   - [RGBColor, RGBColor]: range from first to second color
 * @param size - Image dimensions:
 *   - number: creates square image of {size, size}
 *   - [width, height]: rectangular dimensions
 * @param seed - Random seed for reproducibility (optional, random if not provided)
 * @returns ImageData containing random pixel values
 *
 * @example
 * ```ts
 * // 150x150 truly random image, black to white
 * const img1 = RandomImage()
 *
 * // Single color: black to provided color
 * const img2 = RandomImage({ r: 100, g: 150, b: 200, a: 255 })
 *
 * // Two colors: range between them, with seed for reproducibility
 * const img3 = RandomImage(
 *   [{ r: 50, g: 50, b: 50, a: 255 }, { r: 200, g: 200, b: 200, a: 255 }],
 *   [256, 256],
 *   42
 * )
 * ```
 */
export const RandomImage = (
  color: ColorRange = WHITE,
  size: ImageSize = DEFAULT_SIZE,
  seed?: number
): ImageData => {
  if (!IsArray(color)) {
    return RandomImage([BLACK, color], size, seed)
  }

  const [{ r: r1, g: g1, b: b1, a: a1 }, { r: r2, g: g2, b: b2, a: a2 }] = color
  const [width, height] = normalizeSize(size)
  const n = width * height

  const pixels = Zip(
    Range(0, (n - 1) * 4, 4),
    randomChannel(n, r1, r2, seed),
    randomChannel(n, g1, g2, IsNumber(seed) ? seed + 1 : undefined),
    randomChannel(n, b1, b2, IsNumber(seed) ? seed + 2 : undefined),
    randomChannel(n, a1, a2, IsNumber(seed) ? seed + 3 : undefined)
  )

  const data = new Uint8ClampedArray(n * 4)

  for (const [i, r, g, b, a] of pixels) {
    data[i] = r
    data[i + 1] = g
    data[i + 2] = b
    data[i + 3] = a
  }

  return new ImageData(data, width, height)
}
